import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom, timeout } from 'rxjs';
import { CertificateAuthorityDto, CertificateAuthorityService } from './certificate-authority-service';
import { DateTimeInputComponent } from '../../../shared/commons/date-time-input/date-time-input.component';
import { formatRecordedAt, resolveRecordedBy } from '../../../shared/commons/audit-display.util';

@Component({
  selector: 'app-certificate-authority-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DateTimeInputComponent],
  templateUrl: './certificate-authority-modal.html'
})
export class CertificateAuthorityModal implements OnInit {
  @Input() certificateAuthority: CertificateAuthorityDto | undefined;
  @Input() viewOnly = false;
  @Input() onSave?: (payload: any, isEditMode: boolean) => Promise<boolean>;

  certificateAuthorityForm: FormGroup;
  isEditMode = false;
  submitted = false;
  isSubmitting = false;
  recordedByDisplay = '-';
  recordedAtDisplay = '-';
  selectedFile: File | null = null;
  previewing = false;
  uploadingForSave = false;
  uploadErrorMessage = '';
  hasUploadedCertificate = false;
  uploadedCertificateFileName = '';
  readonlyCertificateFields = false;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private certificateAuthorityService: CertificateAuthorityService,
    private cdr: ChangeDetectorRef
  ) {
    this.certificateAuthorityForm = this.fb.group({
      id: [null],
      issuerCn: ['', [Validators.required, Validators.maxLength(255)]],
      issuerOu: ['', [Validators.maxLength(255)]],
      issuerO: ['', [Validators.maxLength(255)]],
      issuerL: ['', [Validators.maxLength(255)]],
      issuerC: ['', [Validators.maxLength(255)]],
      subjectCn: ['', [Validators.required, Validators.maxLength(255)]],
      subjectOu: ['', [Validators.maxLength(255)]],
      subjectO: ['', [Validators.maxLength(255)]],
      subjectL: ['', [Validators.maxLength(255)]],
      subjectC: ['', [Validators.maxLength(255)]],
      validFrom: [null as string | null],
      validTo: [null as string | null],
      revocationUrl: ['', [Validators.maxLength(500)]],
      certificateFile: ['', [Validators.maxLength(500)]],
      ordering: [0],
      state: [1]
    });
  }

  ngOnInit(): void {
    if (this.certificateAuthority) {
      this.isEditMode = true;
      this.hasUploadedCertificate = true;
      this.uploadedCertificateFileName = this.certificateAuthority.certificateFileName ?? '';
      this.recordedByDisplay = resolveRecordedBy(this.certificateAuthority);
      this.recordedAtDisplay = formatRecordedAt(this.certificateAuthority);
      this.certificateAuthorityForm.patchValue({
        ...this.certificateAuthority,
        validFrom: this.toDateInputValue(this.certificateAuthority.validFrom),
        validTo: this.toDateInputValue(this.certificateAuthority.validTo)
      });
    } else {
      this.certificateAuthorityForm.reset({ ordering: 0, state: 1 });
      this.recordedByDisplay = '-';
      this.recordedAtDisplay = '-';
    }

    this.readonlyCertificateFields = this.hasUploadedCertificate;

    if (this.viewOnly) {
      this.certificateAuthorityForm.disable();
    }
  }

  save(): void {
    if (this.viewOnly) {
      return;
    }

    if (!this.isEditMode && !this.hasUploadedCertificate) {
      this.uploadErrorMessage = 'กรุณาอัปโหลดไฟล์ใบรับรองก่อนบันทึกข้อมูล';
      return;
    }

    this.submitted = true;
    if (this.certificateAuthorityForm.invalid) {
      return;
    }

    const rawValue = this.certificateAuthorityForm.getRawValue();
    const payload: CertificateAuthorityDto = {
      ...rawValue,
      validFrom: this.toIsoDateTime(rawValue.validFrom as string | NgbDateStruct | null),
      validTo: this.toIsoDateTime(rawValue.validTo as string | NgbDateStruct | null)
    };

    void this.submitAndClose(payload);
  }

  async onCertificateSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }

    this.selectedFile = file;
    this.previewing = true;
    this.uploadErrorMessage = '';

    try {
      const preview = await firstValueFrom(
        this.certificateAuthorityService.previewCertificateAuthority(file).pipe(timeout(20000))
      );
      this.certificateAuthorityForm.patchValue({
        issuerCn: preview.issuerCn ?? '',
        issuerOu: preview.issuerOu ?? '',
        issuerO: preview.issuerO ?? '',
        issuerL: preview.issuerL ?? '',
        issuerC: preview.issuerC ?? '',
        subjectCn: preview.subjectCn ?? '',
        subjectOu: preview.subjectOu ?? '',
        subjectO: preview.subjectO ?? '',
        subjectL: preview.subjectL ?? '',
        subjectC: preview.subjectC ?? '',
        validFrom: this.toDateInputValue(preview.validFrom),
        validTo: this.toDateInputValue(preview.validTo)
      });
      this.hasUploadedCertificate = true;
      this.uploadedCertificateFileName = preview.certificateFileName ?? file.name;
      this.readonlyCertificateFields = true;
    } catch {
      this.hasUploadedCertificate = this.isEditMode && !!this.certificateAuthorityForm.get('certificateFile')?.value;
      this.uploadedCertificateFileName = '';
      this.uploadErrorMessage = 'ไม่สามารถอัปโหลดหรืออ่านรายละเอียดจากไฟล์ใบรับรองที่เลือกได้';
      this.readonlyCertificateFields = this.hasUploadedCertificate;
    } finally {
      this.previewing = false;
      this.cdr.detectChanges();
    }
  }

  getCertificateDownloadUrl(): string {
    return this.certificateAuthorityForm.get('certificateFile')?.value ?? '';
  }

  hasSecureDownloadTarget(): boolean {
    const id = this.certificateAuthorityForm.get('id')?.value;
    return Number.isInteger(id);
  }

  getCertificateDownloadFileName(): string {
    if (this.uploadedCertificateFileName) {
      return this.uploadedCertificateFileName;
    }
    if (this.certificateAuthority?.certificateFileName) {
      return this.certificateAuthority.certificateFileName;
    }
    return 'certificate.crt';
  }

  async downloadCurrentCertificate(event?: Event): Promise<void> {
    event?.preventDefault();
    const id = this.certificateAuthorityForm.get('id')?.value;
    if (!Number.isInteger(id)) {
      return;
    }
    try {
      const blob = await firstValueFrom(this.certificateAuthorityService.downloadCertificateAuthorityFile(id));
      const fileName = this.getCertificateDownloadFileName();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
    } catch {
      this.uploadErrorMessage = 'ไม่สามารถดาวน์โหลดไฟล์ใบรับรองได้';
    }
  }

  private toDateInputValue(value?: string): string | null {
    if (!value) {
      return null;
    }

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
      return null;
    }

    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  private toIsoDateTime(value?: string | NgbDateStruct | null): string | undefined {
    if (!value) {
      return undefined;
    }

    if (typeof value === 'string') {
      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!match) {
        return undefined;
      }
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      const date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
      return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
    }

    if (!Number.isInteger(value.year) || !Number.isInteger(value.month) || !Number.isInteger(value.day)) {
      return undefined;
    }

    const date = new Date(Date.UTC(value.year, value.month - 1, value.day, 0, 0, 0));
    if (Number.isNaN(date.getTime())) {
      return undefined;
    }

    return date.toISOString();
  }

  private async submitAndClose(payload: any): Promise<void> {
    if (!this.onSave) {
      this.activeModal.close(payload);
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    try {
      const payloadWithUploadedCertificate = await this.uploadSelectedCertificateIfNeeded(payload);
      if (!payloadWithUploadedCertificate) {
        return;
      }

      const isSuccess = await this.onSave(payloadWithUploadedCertificate, this.isEditMode);
      if (isSuccess) {
        this.activeModal.close(payloadWithUploadedCertificate);
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  private async uploadSelectedCertificateIfNeeded(payload: CertificateAuthorityDto): Promise<CertificateAuthorityDto | null> {
    if (!this.selectedFile) {
      return payload;
    }

    this.uploadingForSave = true;
    this.uploadErrorMessage = '';
    try {
      const uploaded = await firstValueFrom(
        this.certificateAuthorityService.uploadCertificateAuthority(this.selectedFile).pipe(timeout(20000))
      );
      const certificateFile = uploaded.certificateFile ?? payload.certificateFile;
      const certificateFileName = uploaded.certificateFileName ?? this.selectedFile.name;
      this.certificateAuthorityForm.patchValue({ certificateFile });
      this.uploadedCertificateFileName = certificateFileName;

      return {
        ...payload,
        certificateFile,
        certificateFileName
      };
    } catch {
      this.uploadErrorMessage = 'ไม่สามารถอัปโหลดไฟล์ใบรับรองเพื่อบันทึกข้อมูลได้';
      return null;
    } finally {
      this.uploadingForSave = false;
      this.cdr.detectChanges();
    }
  }
}
